import React, {
  SetStateAction, useEffect, useRef, useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { CKEditorEventHandler } from 'ckeditor4-react'
import Editor from 'shared/components/Editor'
import FormModal from '../../../shared/components/FormModal'
import Input from '../../../components/Form/Input'
import FileUploadDropzone from '../../../components/FileUploadDropzone'
import {
  NewsPost, NewsPostCreateRequest, NewsPostUpdateRequest,
} from '../models/types'
import { containsImage, stripInlineImages } from '../models/newsAttachments'
import {
  buildAttachmentReference, createImageHtml, parseAttachmentReference, referencedAttachmentIds,
  rewriteImageSources, withoutQuery,
} from '../models/newsInlineImages'
import { useNewsAttachmentUrls } from '../api/fetchNewsAttachmentUrl'
import useNewsPostSave from './useNewsPostSave'

// Pasted and dropped images become attachments instead of base64 strings inside the body.
const editorConfig = {
  clipboard_handleImages: false,
  pasteFromWord_inlineImages: false,
}

interface PasteData {
  dataValue?: string
  method?: 'paste' | 'drop'
  dataTransfer?: {
    getFilesCount: () => number
    getFile: (index: number) => File
  }
}

interface NewsEditorInstance {
  getData: () => string
  setData: (html: string) => void
}

interface CKEditorNamespace {
  plugins: { clipboard: { addFileMatcher: (editor: unknown, matcher: (file: File) => boolean) => void } }
}

// With the editor's own image handling off, CKEditor announces every pasted image as an
// unsupported format, although this form turns it into an attachment. Declaring images supported
// silences that; other file types still get the notice, and are still ignored.
const acceptImagesAsFiles = (editor: unknown) => {
  const namespace = (window as unknown as { CKEDITOR?: CKEditorNamespace }).CKEDITOR
  namespace?.plugins.clipboard.addFileMatcher(editor, (file) => file.type.startsWith('image/'))
}

// What an image in the editor stands for: a chosen file (pasted in this form) and, once stored, its
// attachment. Keyed by the image's `src` in the editor — a blob URL or a resolved download link.
interface InlineImage {
  key?: string
  attachmentId?: number
}

interface NewsFormModalProps {
  show: boolean
  post: NewsPost | null
  onCreate: (draft: NewsPostCreateRequest) => Promise<NewsPost>
  onUpdate: (id: number, draft: NewsPostUpdateRequest) => Promise<NewsPost | null>
  onInvalidate: () => Promise<void>
  onClose: () => void
}

function NewsFormModal({
  show,
  post,
  onCreate,
  onUpdate,
  onInvalidate,
  onClose,
}: Readonly<NewsFormModalProps>) {
  const { t } = useTranslation()
  const [title, setTitle] = useState<string>(post?.title ?? '')
  const [body, setBody] = useState<string>(post?.body ?? '')
  const editorRef = useRef<NewsEditorInstance | null>(null)
  const imagesRef = useRef(new Map<string, InlineImage>())
  // The editor reads its content once, so an existing post's references are resolved to links first.
  const referencedIds = useRef(post ? referencedAttachmentIds(post.body, post.id) : []).current
  const { urls, isSettled } = useNewsAttachmentUrls(post?.id ?? 0, referencedIds)
  const [isEditorReady, setEditorReady] = useState<boolean>(referencedIds.length === 0)

  useEffect(() => {
    if (isEditorReady || !isSettled || post === null) {
      return
    }
    const resolved = rewriteImageSources(post.body, (src) => {
      const reference = parseAttachmentReference(src)
      const url = reference && reference.postId === post.id ? urls.get(reference.attachmentId) : undefined
      if (reference === null || url === undefined) {
        return src
      }
      imagesRef.current.set(url, { attachmentId: reference.attachmentId })
      return url
    })
    setBody(resolved)
    setEditorReady(true)
  }, [isEditorReady, isSettled, post, urls])

  useEffect(() => () => {
    imagesRef.current.forEach((_, src) => {
      if (src.startsWith('blob:')) {
        URL.revokeObjectURL(src)
      }
    })
  }, [])

  const findImage = (src: string): InlineImage | undefined => {
    const exact = imagesRef.current.get(src)
    if (exact || !/^https?:/i.test(src)) {
      return exact
    }
    const bare = withoutQuery(src)
    return Array.from(imagesRef.current.entries()).find(([candidate]) => withoutQuery(candidate) === bare)?.[1]
  }

  const currentHtml = () => editorRef.current?.getData() ?? body

  const replaceHtml = (html: string) => {
    setBody(html)
    editorRef.current?.setData(html)
  }

  const inlineImages = {
    isInline: (key: string) => Array.from(imagesRef.current.values()).some((image) => image.key === key),
    onUploaded: (key: string, attachment: { id: number }) => {
      imagesRef.current.forEach((image) => {
        if (image.key === key) {
          // eslint-disable-next-line no-param-reassign
          image.attachmentId = attachment.id
        }
      })
    },
    getBody: (postId: number | null) => {
      const html = currentHtml()
      if (postId === null) {
        return html
      }
      return rewriteImageSources(html, (src) => {
        const attachmentId = findImage(src)?.attachmentId
        return attachmentId === undefined ? src : buildAttachmentReference(postId, attachmentId)
      })
    },
  }
  // Offered only when creating, and off by default: notifying everyone again about an edited post
  // is not something an accidental click should be able to produce.
  const [shouldNotify, setShouldNotify] = useState<boolean>(false)
  const {
    savedPost,
    keptAttachments,
    chosenFiles,
    rejections,
    hasRejection,
    isSaving,
    isGone,
    addFiles,
    removeChosenFile,
    removeKeptAttachment,
    save,
    requestClose,
  } = useNewsPostSave({
    post, onCreate, onUpdate, onInvalidate, inlineImages,
  })

  // Removing a file removes its images from the text; deleting an image from the text keeps the file.
  const removeImages = (matches: (image: InlineImage | undefined, src: string) => boolean) => {
    const html = currentHtml()
    const next = rewriteImageSources(html, (src) => (matches(findImage(src), src) ? null : src))
    if (next !== html) {
      replaceHtml(next)
    }
  }

  const handleRemoveChosenFile = (key: string) => {
    removeChosenFile(key)
    removeImages((image) => image?.key === key)
  }

  const handleRemoveKeptAttachment = (id: number) => {
    removeKeptAttachment(id)
    removeImages((image, src) => image?.attachmentId === id || parseAttachmentReference(src)?.attachmentId === id)
  }
  const isLocked = isSaving || isGone
  const isCreating = savedPost === null

  // react-dropzone hands over only the files of the latest drop, so they are appended.
  const acceptDroppedFiles = (value: SetStateAction<File[]>) => {
    addFiles(typeof value === 'function' ? value([]) : value)
  }

  // When the pasted HTML still shows the image (e.g. a copied web image linked by address), the
  // clipboard's file copy of it is ignored rather than attached a second time.
  const handleEditorPaste: CKEditorEventHandler<'paste'> = (evt) => {
    const data = evt.data as PasteData | null | undefined
    if (!data) {
      return
    }

    data.dataValue = stripInlineImages(data.dataValue ?? '')
    if (!data.dataTransfer || containsImage(data.dataValue)) {
      return
    }

    const images: File[] = []
    for (let index = 0; index < data.dataTransfer.getFilesCount(); index += 1) {
      const file = data.dataTransfer.getFile(index)
      if (file.type.startsWith('image/')) {
        images.push(file)
      }
    }
    // Shown in place from a local preview until the file is uploaded and referenced.
    const previews = addFiles(images).map((chosen) => {
      const src = URL.createObjectURL(chosen.file)
      imagesRef.current.set(src, { key: chosen.key })
      return createImageHtml(src, chosen.file.name)
    })
    data.dataValue += previews.join('')
  }

  const handleClose = async () => {
    if (await requestClose()) {
      onClose()
    }
  }

  const rowError = (index: number) => {
    const rejection = rejections[index]
    if (rejection) {
      return t(`news.attachment_${rejection.reason}`, rejection)
    }
    const { uploadFailure } = chosenFiles[index]
    return uploadFailure ? t(`news.attachment_${uploadFailure}`) : null
  }

  return (
    <FormModal
      show={show}
      title={isCreating ? t('news.addTitle') : t('news.editTitle')}
      onClose={handleClose}
      size="lg"
      enforceFocus={false}
      dialogClassName="modal news-form-modal"
    >
      <div className="mb-3">
        <label className="form-label" htmlFor="newsPostTitle_input">
          {t('news.postTitle')}
        </label>
        <Input
          name="newsPostTitle"
          value={title}
          disabled={isLocked}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTitle(event.target.value)}
        />
      </div>
      <div className="mb-3">
        <span className="form-label d-block">{t('news.attachments')}</span>
        {keptAttachments.length > 0 && (
          <ul className="news-form-modal__attachments" data-testid="news-kept-attachments">
            {keptAttachments.map((attachment) => (
              <li key={attachment.id}>
                <span className="me-2">{attachment.filename}</span>
                <button
                  type="button"
                  className="btn btn-link btn-sm px-1"
                  title={t('buttons.remove')}
                  // Until the editor holds the text, removing the image from it would be overwritten.
                  disabled={isLocked || !isEditorReady}
                  onClick={() => handleRemoveKeptAttachment(attachment.id)}
                >
                  <i className="fal fa-times" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {chosenFiles.length > 0 && (
          <ul className="news-form-modal__attachments" data-testid="news-pending-attachments">
            {chosenFiles.map((chosen, index) => {
              const error = rowError(index)
              return (
                <li key={chosen.key}>
                  <span className="me-2">{chosen.file.name}</span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm px-1"
                    title={t('buttons.remove')}
                    disabled={isLocked}
                    onClick={() => handleRemoveChosenFile(chosen.key)}
                  >
                    <i className="fal fa-times" />
                  </button>
                  {error !== null && (
                    <div className="form-text text-danger" data-testid="news-attachment-error">{error}</div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        <FileUploadDropzone setAcceptedFiles={acceptDroppedFiles} disabled={isLocked} showAcceptedFiles={false} />
      </div>
      {isEditorReady ? (
        <Editor
          editorContent={body}
          setEditorContent={setBody}
          className="news-form-modal__editor"
          name="news-editor"
          config={editorConfig}
          onPaste={handleEditorPaste}
          readOnly={isLocked}
          onInstanceReady={({ editor }) => {
            editorRef.current = editor
            acceptImagesAsFiles(editor)
          }}
        />
      ) : (
        <div className="news-form-modal__editor d-flex justify-content-center align-items-center" data-testid="news-editor-loading">
          <i className="fal fa-spinner fa-spin fa-2x" />
        </div>
      )}
      <div className="d-flex justify-content-end align-items-center mt-3">
        {isCreating && (
          <div className="form-check me-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="newsNotifyByEmail"
              checked={shouldNotify}
              disabled={isLocked}
              onChange={(event) => setShouldNotify(event.target.checked)}
              data-testid="news-notify"
            />
            <label className="form-check-label text-nowrap" htmlFor="newsNotifyByEmail">
              {t('news.notifyByEmail')}
            </label>
          </div>
        )}
        <button
          type="button"
          className="btn btn-dark btn-lg m-0 text-primary"
          disabled={isLocked || !isEditorReady || title.trim() === '' || hasRejection}
          onClick={async () => {
            if (await save(title.trim(), isCreating && shouldNotify)) {
              onClose()
            }
          }}
          data-testid="news-save"
        >
          {isSaving && <i className="fal fa-spinner fa-spin" />}
          {!isSaving && <span>{t('buttons.save')}</span>}
        </button>
      </div>
    </FormModal>
  )
}

export default NewsFormModal
